import React, { useState, useEffect, useCallback, useMemo } from 'react';
import apiClient from '../../utils/api';
import { localToday } from '../../utils/date';
import { DATA_RANGES, RangeSelector, useTimeRange } from './TimeRange';
import { EntryRow } from '../log/LoggedToday';

// The record of what you've logged, structured the way you logged it: by day, then
// (for food) grouped into meals, and everything else grouped by what it is. The parser
// now tags food with a meal_type, so recent days show real meals; older entries that
// predate that tagging simply fall under "Food" — nothing is lost, it's just flatter.
//
// This is a READ surface. It shows what YOU logged — device/wearable auto-syncs (Fitbit
// steps, sleep, etc.) live in their own cards on Home/Data and would only bury the intent
// here, so they're filtered out.
//
// It's a Data tab now (it used to be a second tab on the Log page, which cost a permanent
// tab bar on the smallest screen we have to review data on the screen meant for capture).
// So it takes the same shared time range as every other Data tab instead of its own
// two-weeks-then-"load earlier" window — one control, remembered across tabs.

// The server caps a page at 500 rows. A wide range on a busy account can hit that, and a
// silently truncated history reads as lost data, so we say so when it happens.
const PAGE_LIMIT = 500;

// Parse a YYYY-MM-DD as a LOCAL calendar day (never `new Date(str)`, which is UTC and can
// shift the day across the timezone boundary — the same trap localToday() avoids).
const parseLocal = (s) => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const shiftDays = (s, delta) => {
  const d = parseLocal(s);
  d.setDate(d.getDate() + delta);
  const y = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${mm}-${dd}`;
};

const dayHeading = (dateStr) => {
  const today = localToday();
  if (dateStr === today) return 'Today';
  if (dateStr === shiftDays(today, -1)) return 'Yesterday';
  const d = parseLocal(dateStr);
  const weekday = d.toLocaleDateString(undefined, { weekday: 'long' });
  const rest = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return `${weekday}, ${rest}`;
};

const caloriesOf = (e) => {
  if (e.calories != null) return e.calories;
  if (e.value != null && e.value_unit === 'kcal') return e.value;
  // Older entries (logged before calories were promoted to the column) can still carry the
  // number in the JSONB blob — read it so their totals aren't silently zero.
  if (e.data && e.data.calories != null) return e.data.calories;
  return null;
};

const round = (n) => (Math.round(n * 10) / 10);

const LogHistory = ({ refreshKey }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [truncated, setTruncated] = useState(false);
  const [query, setQuery] = useState('');

  const { selectorProps } = useTimeRange();
  const { rangeKey, customStart, customEnd } = selectorProps;

  // The range picks the fetch window directly: history rows are far heavier
  // than a datapoint, and the endpoint already takes a date window.
  const [startParam, endParam] = useMemo(() => {
    const today = localToday();
    if (rangeKey === 'ALL') return [null, today];
    if (rangeKey === 'CUSTOM') return [customStart || null, customEnd || today];
    const days = DATA_RANGES.find((r) => r.key === rangeKey)?.days;
    return [days ? shiftDays(today, -days) : null, today];
  }, [rangeKey, customStart, customEnd]);

  const fetchWindow = useCallback(async () => {
    setLoading(true);
    try {
      const params = { end_date: endParam, limit: PAGE_LIMIT };
      if (startParam) params.start_date = startParam;
      const res = await apiClient.get('/api/health/entries', { params });
      const raw = res.data || [];
      setTruncated(raw.length >= PAGE_LIMIT);
      setEntries(raw);
    } catch (err) {
      console.error('Error loading log history:', err);
    } finally {
      setLoading(false);
    }
  }, [startParam, endParam]);

  useEffect(() => { fetchWindow(); }, [fetchWindow, refreshKey]);

  // Search your own words: what you said, what it became, any note.
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter((e) =>
      [e.raw_input, e.entry_type, e.notes, e.data?.food_name, e.category_slug]
        .some((f) => f && String(f).toLowerCase().includes(q)));
  }, [entries, query]);

  const days = useMemo(() => {
    const byDate = new Map();
    for (const e of shown) {
      if (!byDate.has(e.entry_date)) byDate.set(e.entry_date, []);
      byDate.get(e.entry_date).push(e);
    }
    const sortItems = (a, b) => (b.entry_time || '').localeCompare(a.entry_time || '') || (b.created_at || '').localeCompare(a.created_at || '');
    return [...byDate.entries()].map(([date, items]) => ({ date, items: items.sort(sortItems), calories: items.reduce((s, e) => s + (caloriesOf(e) || 0), 0) }));
  }, [shown]);

  return (
    <section>
      <label className="mb-3.5 flex items-center gap-3 rounded-full border border-line-strong px-[18px] py-3 text-fg-3 focus-within:border-fg-3">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" className="h-5 w-5 flex-none" aria-hidden="true">
          <circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" />
        </svg>
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search what you’ve said, like “pizza” or “headache”"
          className="w-full bg-transparent text-base text-fg placeholder:text-fg-3 focus:outline-none"
        />
      </label>

      <RangeSelector {...selectorProps} />

      {truncated && (
        <p className="mt-3 text-sm text-fg-3">Showing the {PAGE_LIMIT} most recent entries in this range. Narrow the range to see further back.</p>
      )}

      {loading ? (
        <div className="mt-[18px] h-32 animate-pulse rounded-xl border border-line" />
      ) : days.length === 0 ? (
        <div className="mt-[18px] rounded-xl border border-line px-5 py-5 text-fg-3">
          {query ? `Nothing matches “${query}” in this range.` : 'Nothing logged in this range. Widen it above, or log something on the Log page.'}
        </div>
      ) : (
        days.map((day) => (
          <div key={day.date} className="mt-[18px]">
            <h3 className="mb-2 flex justify-between text-base font-medium text-fg-2">
              {dayHeading(day.date)}
              <span className="font-normal text-fg-3">
                {day.items.length} thing{day.items.length === 1 ? '' : 's'}{day.calories > 0 ? ` · ${round(day.calories)} cal` : ''}
              </span>
            </h3>
            <div className="divide-y divide-line rounded-xl border border-line">
              {day.items.map((e) => <EntryRow key={e.id} entry={e} />)}
            </div>
          </div>
        ))
      )}
    </section>
  );
};

export default LogHistory;
